import { get, groupBy, keyBy, startCase } from 'lodash';
import { interventions } from '../../config/interventions';
import { caseLogicMsg } from '../validationMessages';

function validateInterventionNodes(caseConf, nodeInfo) {
  const availableInterventions = caseConf.availableInterventions || [];
  const availableInterventionsByKey = keyBy(availableInterventions, 'key');
  const interventionsByKey = keyBy(interventions, 'key');
  const interventionsByCategory = groupBy(interventions, 'category');
  const language = 'en';
  const errors = [];
  const warnings = [];
  
  function isInterventionAvailable(key) {
    return availableInterventionsByKey[key] ? availableInterventionsByKey[key].available : false
  }  

  // Actions
  const interventionActionNodes = nodeInfo.actionNodes.filter(node => node.data && node.data.action && node.data.action.type === 'INTERVENTION');
  interventionActionNodes.forEach(node => {
    const action = get(node, 'data.action');
    if (!action.id) {
      errors.push(caseLogicMsg('You need to select an intervention', node.id));
    }
    const intervention = interventionsByKey[action.id];
    if (intervention && intervention.key) {
      if (!isInterventionAvailable(intervention.key)) {
        const labelFormatted = intervention.label[language] || '';
        const message = caseLogicMsg(`Intervention ${labelFormatted} is used in case logic but is not enabled`, node.id );
        warnings.push(message); 
      }
    }
  });

  // Behavior effects
  const behaviorChangeNodes = nodeInfo.effectNodes.filter(
    (node) => {
      const effectType = get(node, 'data.effect.type');
      return  effectType === 'ANIMATION_REPEAT' || effectType === 'ANIMATION_ONE_SHOT'
    }
  );

  let foundCaseLogicError = false;
  for (const availIntv of availableInterventions || []) {
    const intv = interventions.find(intv => intv.key === availIntv.key);
    if (intv && intv.availableStances) {
      for (const node of behaviorChangeNodes) {
        const position = get(node, 'data.effect.position');
        if (!intv.availableStances.includes(position) && !foundCaseLogicError) {
          errors.push(
            caseLogicMsg('Position not compatible with selected interventions.', node.id, { treatment: intv.treatment})
          );
          foundCaseLogicError = true;
        }
      }
    }
  }

  // Checks
  const interventionCheckNodes = nodeInfo.checkNodes.flatMap(node =>
    Object.values(node.data)
      .filter(dataItem => dataItem.type === 'INTERVENTION_ADMINISTERED')
      .map(dataItem => ({ nodeId: node.id, ...dataItem }))
  );
  if (interventionCheckNodes.length > 0) {
    // Inject category and treatment into availableInterventions items
    const availableInterventionsWithData = availableInterventions.map(availableIntervention => {
      return {
        category: interventionsByKey[availableIntervention.key].category,
        treatment: interventionsByKey[availableIntervention.key].treatment,
        ...availableIntervention
      }
    });
    
    interventionCheckNodes.forEach(node => {
      const { category, nodeId, treatments, types } = node;
      if (treatments.length === 0) {
        // If "any treatment" is selected within a category
        const availableInterventionsByCategory = groupBy(availableInterventionsWithData, 'category');
        if (!availableInterventionsByCategory[category]) {
          const formattedCategory = startCase(category.toLowerCase());
          const message = caseLogicMsg(`Intervention category ${formattedCategory} is used in case logic but is not enabled`, nodeId, { category });
          warnings.push(message); 
        }
      }
      else {
        treatments.forEach(treatment => {
          if (types.length === 0) {
            // If "any type" is selected within a treatment
            const availableInterventionsByTreatment = groupBy(availableInterventionsWithData, 'treatment');
            if (!availableInterventionsByTreatment[treatment]) {
              const formattedTreatment = startCase(treatment.toLowerCase());
              const message = caseLogicMsg(`Intervention treatment ${formattedTreatment} is used in case logic but is not enabled`, nodeId, { treatment });
              warnings.push(message); 
            }    
          }
          types.forEach(type => {
            // Find intervention by category + type
            const intervention = interventionsByCategory[category].find(intervention => intervention.treatment === treatment && intervention.type === type);
            if (intervention && intervention.key) {
              if (!isInterventionAvailable(intervention.key)) {
                const labelFormatted = intervention.label[language] || '';
                const message = caseLogicMsg(`Intervention ${labelFormatted} is used in case logic but is not enabled`, nodeId, { treatment });
                warnings.push(message); 
              }
            }
          })
        }) 
      }
      
    })
  }

  return {
    errors,
    warnings,
  };
}

export default validateInterventionNodes;